import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../utils/api";
import { useCountry } from "../context/CountryContext";

export default function TransactionDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isGhana, config } = useCountry();

  const [tx, setTx] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🎨 THEME PER COUNTRY
  const theme = isGhana
    ? {
        successIconBg: "bg-yellow-500/20",
        successIconDot: "bg-yellow-500",
        statusConfirmed: "text-yellow-400",
        actionAccent: "text-yellow-400",
        reportBtn: "bg-yellow-900",
        shareBtn: "bg-yellow-500 hover:bg-yellow-600 text-black",
        currencySymbol: "₵",
        walletDefault: "MoMo Wallet",
        flag: "🇬🇭",
      }
    : {
        successIconBg: "bg-green-500/20",
        successIconDot: "bg-green-500",
        statusConfirmed: "text-green-400",
        actionAccent: "text-green-400",
        reportBtn: "bg-green-800",
        shareBtn: "bg-green-600 hover:bg-green-700 text-white",
        currencySymbol: "₦",
        walletDefault: "OWealth",
        flag: "🇳🇬",
      };

  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        const res = await API.get(`/bank/wallet/transaction/${id}`);
        setTx(res.data.transaction);
      } catch (err) {
        console.error("❌ Error fetching transaction:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchTransaction();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        <p className="animate-pulse">Loading Transaction...</p>
      </div>
    );
  }

  if (!tx) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-white">
        Transaction not found
      </div>
    );
  }

  // Use transaction's own currency symbol if available, otherwise country default
  const txCurrencySymbol = tx.currencySymbol || theme.currencySymbol;

  return (
    <div className="min-h-screen bg-black text-white">

      {/* Country indicator badge */}
      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-zinc-900/60 border border-zinc-700/50 backdrop-blur-md">
        <span className="text-base leading-none">{theme.flag}</span>
        <span className="text-xs text-slate-300 font-medium">{config.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-gray-800">
        <button onClick={() => navigate(-1)} className="text-white text-lg font-bold">
          ←
        </button>
        <h1 className="text-lg font-bold">Transaction Details</h1>
        <div className="w-6" />
      </div>

      {/* Main Section */}
      <div className="p-5">
        {/* Centered Icon */}
        <div className="flex justify-center mb-3">
          <div className={`${theme.successIconBg} p-3 rounded-full`}>
            <div className={`${theme.successIconDot} w-5 h-5 rounded-full`} />
          </div>
        </div>

        {/* Amount + Status */}
        <p className="text-center text-gray-300 text-sm">
          Transfer to {tx.account_name || "Recipient"}
        </p>
        <h2 className="text-center text-3xl font-bold mt-1">
          {txCurrencySymbol}{Number(tx.amount).toLocaleString()}
        </h2>
        <p
          className={`text-center mt-2 font-semibold ${
            tx.status === "confirmed"
              ? theme.statusConfirmed
              : tx.status === "failed"
              ? "text-red-400"
              : "text-yellow-400"
          }`}
        >
          {tx.status?.charAt(0).toUpperCase() + tx.status?.slice(1)}
        </p>

        {/* Transaction Details */}
        <div className="mt-6 bg-gray-900 rounded-2xl p-4 space-y-3">
          <h3 className="font-semibold text-lg border-b border-gray-800 pb-2">
            Transaction Details
          </h3>

          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Recipient Details</span>
            <span className="text-right">
              {tx.account_name || "N/A"} <br />
              {tx.bank_name || ""} {tx.account_number ? `| ${tx.account_number}` : ""}
            </span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Transaction No.</span>
            <span className="text-right break-all max-w-[60%]">{tx._id}</span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Payment Method</span>
            <span className="text-right">{tx.wallet || theme.walletDefault}</span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Country</span>
            <span className="text-right">
              {tx.country === "GH" ? "🇬🇭 Ghana" : tx.country === "NG" ? "🇳🇬 Nigeria" : `${theme.flag} ${config.name}`}
            </span>
          </div>

          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Transaction Date</span>
            <span className="text-right">
              {new Date(tx.createdAt).toLocaleString()}
            </span>
          </div>
        </div>

        {/* More Actions */}
        <div className="mt-4 bg-gray-900 rounded-2xl p-4 space-y-3">
          <h3 className="font-semibold text-lg border-b border-gray-800 pb-2">
            More Actions
          </h3>

          <div className="flex justify-between text-sm">
            <span className="text-gray-400">Category</span>
            <span className="capitalize">{tx.type || "Transfer"}</span>
          </div>

          <div className={`flex items-center justify-between text-sm ${theme.actionAccent} mt-3`}>
            <button
              onClick={() => navigate("/transfer-opay")}
              className="flex items-center gap-1 hover:underline"
            >
              🔁 Transfer Again
            </button>
            <button
              onClick={() => navigate("/opay/history")}
              className="flex items-center gap-1 hover:underline"
            >
              🕒 View Records
            </button>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-around mt-6 px-2">
          <button className={`${theme.reportBtn} w-1/2 mx-1 py-3 rounded-full text-sm font-semibold text-white`}>
            Report Issue
          </button>
          <button className={`${theme.shareBtn} w-1/2 mx-1 py-3 rounded-full text-sm font-semibold`}>
            Share Receipt
          </button>
        </div>
      </div>
    </div>
  );
}
